import Divider from "@ui-kit/data-display/Divider/Divider.web";
import Box from "@ui-kit/layout/Box/Box.web";
import useAssignedSeatingHelpers from "@shared/hooks/seating/useAssignedSeatingHelpers/useAssignedSeatingHelpers";
import useSeatingWaitlistHelpers from "@shared/hooks/seating/useSeatingWaitlistHelpers/useSeatingWaitlistHelpers";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { useTranslation } from "@shared/utils/internationalization";
import { useCallback, useMemo } from "react";
import ItemBody from "../ItemBody/ItemBody.web";
import ItemHeader from "../ItemHeader/ItemHeader.web";
import { CartWaitlistItemProps } from "./CartWaitlistItem.props";

const CartWaitlistItem = (props: CartWaitlistItemProps) => {
  const { t } = useTranslation("seating");

  const { removeSeatWaitlist } = useSeatingWaitlistHelpers();
  const { removeLocationPermission } = useAssignedSeatingHelpers();
  const { getInfoRequiredLocationQtyInCart, seatingEventData } =
    useSeatingState();

  const { formattedData, disableItemDeletion, venueData } = props;

  const waitlistType: string = useMemo(() => {
    if (formattedData.seat) {
      return t("Seat");
    }
    if (formattedData.location) {
      return t("Area");
    }
    return "";
  }, [t, formattedData]);

  // get the full item type object to display item properties
  const itemObj: SeatingItemPropertyMap = useMemo(() => {
    const itemTypes =
      seatingEventData?.ticket_types ??
      seatingEventData?.tickettype_set ??
      seatingEventData?.membership_levels ??
      [];
    return itemTypes?.find(
      (item: { id: number }) =>
        item.id === formattedData.itemGroup?.type ||
        item.id === formattedData.itemGroup?.membership_level
    );
  }, [seatingEventData, formattedData]);

  const getRemoveFunction = useCallback(() => {
    if (formattedData.seat) {
      const { itemGroup } = formattedData;
      const segmentId = Number(itemGroup?.waitlist_seat_segment?.segment_id);
      return () => removeSeatWaitlist(segmentId, itemObj?.id);
    }
    if (formattedData.location) {
      // default final quantity is 0
      let finalQuantity = 0;
      const itemId =
        formattedData.itemGroup?.type ??
        formattedData.itemGroup?.membership_level;

      // info_required items are split in the cart
      // to remove one, we need to reduce the total quantity by - 1
      if (formattedData.itemGroup?.info_required && itemId) {
        finalQuantity = getInfoRequiredLocationQtyInCart(
          itemId,
          Number(formattedData.permissionId),
          1
        );
        // reduce quantity by 1
        finalQuantity -= 1;
      }
      return () =>
        removeLocationPermission(
          itemObj?.id,
          Number(formattedData.permissionId),
          finalQuantity
        );
    }
    return undefined;
  }, [
    formattedData,
    itemObj,
    getInfoRequiredLocationQtyInCart,
    removeSeatWaitlist,
    removeLocationPermission,
  ]);

  const headerVals = useMemo(
    () => [
      {
        id: 0,
        title: t("Section"),
        value:
          formattedData.seat?.segment_identifier ??
          formattedData.location?.segment_identifier ??
          "",
      },
      {
        id: 1,
        title: t("Qty"),
        value: formattedData.itemGroup?.quantity ?? 0,
      },
      {
        id: 2,
        title: t("Type"),
        value: waitlistType,
      },
    ],
    [t, formattedData, waitlistType]
  );

  return (
    <Box
      m="0px 20px"
      p="xs"
      borderRadius="md"
      backgroundColor="gray.200"
      mb="md"
      key={formattedData.permissionId}
    >
      <ItemHeader
        headerVals={headerVals}
        removeItem={getRemoveFunction()}
        disableRemoveButton={disableItemDeletion}
      />

      <Divider my="xs" />

      <ItemBody
        formattedData={formattedData}
        itemObj={itemObj}
        venueData={venueData}
        seatingEventName={seatingEventData?.name}
        isLimitedVisibility={false}
        isAccessible={false}
      />
    </Box>
  );
};

export default CartWaitlistItem;
